import React from 'react';
import classNames from 'classnames';
import * as googleMapsUtil from '../../util/googleMaps';
import { userLocation } from '../../util/maps';

import css from './LocationAutocompleteInput.module.css';

export const CURRENT_LOCATION_ID = 'current-location';

// When displaying data from the Google Maps Places API, and
// attribution is required next to the results.
// See: https://developers.google.com/places/web-service/policies#powered
export const GeocoderAttribution = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.poweredByGoogle, className);
  return <div className={classes} />;
};

/**
 * A forward geocoding (place name -> coordinates) implementation
 * using the Google Maps Places API.
 */
class GeocoderGoogleMaps {
  constructor() {
    this.sessionToken = null;
  }
  getSessionToken() {
    this.sessionToken =
      this.sessionToken || new window.google.maps.places.AutocompleteSessionToken();
    return this.sessionToken;
  }

  // Public API
  //

  /**
   * Search places with the given name.
   *
   * @param {String} search query for place names
   *
   * @return {Promise<{ search: String, predictions: Array<Object>}>}
   * results of the geocoding, should have the original search query
   * and an array of predictions. The format of the predictions is
   * only relevant for the `getPlaceDetails` function below.
   */
  getPlacePredictions(search, countryLimit, locale, isSuperAdmin) {
    const limitCountriesMaybe = countryLimit
      ? {
          // types: ['address'],
          includedRegionCodes: countryLimit,
        }
      : {};

    return googleMapsUtil
      .getPlacePredictions(search, this.getSessionToken(), limitCountriesMaybe)
      .then(results => {
        const streetLevelTypes = ['street_address', 'premise', 'subpremise'];

        const filteredPredictions = results.predictions.filter(pred =>
          pred.placePrediction?.types?.some(type => streetLevelTypes.includes(type))
        );

        return {
          search,
          predictions: isSuperAdmin ? results.predictions : filteredPredictions,
        };
      });
  }

  /**
   * Get the ID of the given prediction.
   */
  getPredictionId(prediction) {
    if (prediction.predictionPlace) {
      // default prediction defined above
      return prediction.id;
    }
    // prediction from Google Maps Places API
    return prediction.placePrediction.placeId;
  }

  /**
   * Get the address text of the given prediction.
   */
  getPredictionAddress(prediction) {
    if (prediction.predictionPlace) {
      // default prediction defined above
      return prediction.predictionPlace.address;
    }
    // prediction from Google Maps Places API
    return prediction.placePrediction.text.text;
  }

  /**
   * Fetch or read place details from the selected prediction.
   *
   * @param {Object} prediction selected prediction object
   * @param {number} currentLocationBoundsDistance bounds distance for current location
   * @param {string} originalSearch original search text entered by user
   *
   * @return {Promise<util.propTypes.place>} a place object
   */
  getPlaceDetails(prediction, currentLocationBoundsDistance, originalSearch) {
    // Case 1: current location
    if (this.getPredictionId(prediction) === CURRENT_LOCATION_ID) {
      return userLocation().then(latlng => {
        return {
          address: '',
          origin: latlng,
          bounds: googleMapsUtil.locationBounds(latlng, currentLocationBoundsDistance),
        };
      });
    }

    // Case 2: already resolved
    if (prediction.predictionPlace) {
      return Promise.resolve(prediction.predictionPlace);
    }

    // Case 3: fetch from Google Maps Places API
    return googleMapsUtil.getPlaceDetails(this.getPredictionId(prediction)).then(async place => {
      this.sessionToken = null;

      const places = new google.maps.places.Place({ id: this.getPredictionId(prediction) });
      const fields = ['formattedAddress', 'location', 'viewport'];
      await places.fetchFields({ fields });

      const hasPostalCode = places?.addressComponents?.some(comp =>
        comp.types.includes('postal_code')
      );
      if (hasPostalCode) {
        return place;
      }

      // Use the original address text (what was displayed in dropdown) instead of Places API formatted address
      const originalAddress = originalSearch || this.getPredictionAddress(prediction);
      const geocoder = new google.maps.Geocoder();

      return new Promise((resolve, reject) => {
        geocoder.geocode({ location: places.location }, (results, status) => {
          let newFormattedAddress = originalAddress;

          if (status === 'OK' && results?.length) {
            const bestResult = results.find(result =>
              result.address_components?.some(ac => ac.types.includes('postal_code'))
            );

            if (bestResult) {
              const postalComponent = bestResult.address_components.find(ac =>
                ac.types.includes('postal_code')
              );
              const zip = postalComponent?.long_name;

              if (zip && !originalAddress.includes(zip)) {
                const parts = originalAddress.split(',').map(p => p.trim());
                if (parts.length >= 3) {
                  // Merge ZIP into the state part
                  parts[parts.length - 2] = `${parts[parts.length - 2]} ${zip}`;
                  newFormattedAddress = parts.join(', ');
                } else {
                  newFormattedAddress = `${originalAddress}, ${zip}`;
                }
              }
            }
          }
          // Return a new object combining the Place data and the modified address
          resolve({
            ...place,
            address: newFormattedAddress,
          });
        });
      });
    });
  }
}

export default GeocoderGoogleMaps;
